import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import ScrollReveal from "./ScrollReveal";
import { serversStatusUrl } from "@/lib/bffClient";
import { publicAsset } from "@/lib/publicAsset";
import {
    Server,
    Users,
    Gauge,
    Package,
    Flame,
    CalendarClock,
    Activity,
    Map as MapIcon,
    ExternalLink,
    Terminal,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const zrsLogoUrl = publicAsset("/zrs-logo.png");
const CARD_TITLE = "Zaralha server info";
/** Acima disto o spawn de caixas é destacado (taxa base do servidor: 3x). */
const LOOT_BOOST_ABOVE = 3;

/*
 * O BFF (/api/servers/status) devolve etiquetas prontas em PT (playersLabel, wipeLabel, uptimeLabel…)
 * e os valores em bruto (players, wipeDaysAgo, nextWipeDate, fps…). Aqui formatamos a partir dos
 * valores em bruto no idioma do visitante; as etiquetas PT ficam como recurso (respostas antigas/cache).
 */
function formatPlayers(data, t) {
    if (data.players == null || data.maxPlayers == null) return data.playersLabel;
    const queue = data.queue > 0 ? ` · ${t("servers.queue", { n: data.queue })}` : "";
    return `${data.players}/${data.maxPlayers}${queue}`;
}

function formatUptime(data, t) {
    if (data.uptimeSeconds == null) {
        return data.uptimeLabel === "N/D" ? t("servers.notAvailable") : data.uptimeLabel;
    }
    const h = Math.floor(data.uptimeSeconds / 3600);
    const m = Math.floor((data.uptimeSeconds % 3600) / 60);
    return `${h}h ${m}m${data.fps != null ? ` · ${Math.round(data.fps)} FPS` : ""}`;
}

function formatWipe(data, t, lang) {
    if (!data.nextWipeDate) return data.wipeLabel ?? data.nextWipeLabel;
    const d = new Date(`${data.nextWipeDate}T00:00:00Z`);
    const date =
        lang === "pt"
            ? `sexta, ${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}`
            : d.toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });
    const days = data.wipeDaysAgo;
    if (days == null) {
        return data.nextWipeIsToday ? t("servers.wipeToday") : t("servers.nextWipeOnly", { date });
    }
    const since =
        days === 0 ? t("servers.wipeToday") : days === 1 ? t("servers.wipeOneDay") : t("servers.wipeDays", { n: days });
    const next = data.nextWipeIsToday ? t("servers.nextWipeIsToday") : t("servers.nextWipeOn", { date });
    return `${since} · ${next}`;
}

/** Copia com a Clipboard API; sem ela (HTTP, browsers antigos) usa um input escondido + execCommand. */
async function copyText(text) {
    try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* tenta o fallback */
    }
    try {
        const el = document.createElement("input");
        el.value = text;
        el.setAttribute("readonly", "");
        el.setAttribute("aria-hidden", "true");
        Object.assign(el.style, { position: "fixed", top: "0", left: "0", opacity: "0", pointerEvents: "none" });
        document.body.appendChild(el);
        el.select();
        el.setSelectionRange(0, text.length);
        const ok = document.execCommand("copy");
        el.remove();
        return ok;
    } catch {
        return false;
    }
}

function StatRow({ icon: Icon, label, value, valueClassName = "" }) {
    return (
        <div className="group flex items-start gap-3 rounded-lg px-3 py-2.5 text-[13px] text-zrs-offwhite/90 transition-colors hover:bg-white/[0.04]">
            {Icon ? (
                <Icon
                    size={16}
                    className="text-gold/75 shrink-0 mt-0.5 opacity-90 group-hover:text-gold"
                    aria-hidden
                />
            ) : null}
            <span className="text-zinc-500 w-[7.5rem] shrink-0 text-[12px] uppercase tracking-wide">
                {label}
            </span>
            <span
                className={`min-w-0 flex-1 text-right font-medium leading-snug ${valueClassName}`}
            >
                {value ?? "—"}
            </span>
        </div>
    );
}

export default function ServersSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { t, lang } = useI18n();

    const load = useCallback(async () => {
        const url = serversStatusUrl();
        try {
            const res = await fetch(url, {
                headers: { Accept: "application/json" },
            });
            const json = await res.json().catch(() => null);

            if (!res.ok) {
                console.warn("[servers-status]", url, res.status, json);
                setData(null);
                return;
            }

            const okPayload =
                json != null &&
                typeof json === "object" &&
                typeof json.online === "boolean";

            if (!okPayload) {
                console.warn(
                    "[servers-status] resposta inesperada (não-JSON do BFF ou payload inválido)",
                    url,
                    { status: res.status, body: json },
                );
                setData(null);
                return;
            }

            setData(json);
        } catch (e) {
            console.warn("[servers-status]", url, e);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 45_000);
        return () => clearInterval(id);
    }, [load]);

    const online = data?.online === true;

    const onConnect = async () => {
        const command = data?.connectCommand;
        if (!command) return;
        if (await copyText(command)) toast.success(t("servers.copied"));
        else toast.error(t("servers.copyFailed", { command }), { duration: 12_000 });
    };

    return (
        <section
            id="servidores"
            data-testid="servers-section"
            className="relative py-24 md:py-32 px-6 md:px-8"
        >
            <div className="max-w-6xl mx-auto">
                <ScrollReveal>
                    <div className="text-center mb-12 md:mb-14">
                        <div className="text-[11px] uppercase tracking-[0.4em] text-gold/80 mb-3">
                            {t("servers.eyebrow")}
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold text-zrs-offwhite">
                            {t("servers.title")}
                        </h2>
                    </div>
                </ScrollReveal>

                <ScrollReveal>
                    <div
                        data-testid="server-card-main"
                        className="gold-border-hover group/card relative max-w-xl mx-auto overflow-hidden rounded-2xl border border-white/[0.09] bg-[#111214] shadow-[0_24px_80px_rgba(0,0,0,0.55)] transition-[transform,box-shadow] duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_28px_96px_rgba(0,0,0,0.65)]"
                    >
                        {/* Faixa tipo banner Discord — logo ZRS */}
                        <div className="relative h-36 md:h-40 overflow-hidden">
                            <div
                                className="absolute inset-0 scale-[1.08] bg-[length:min(120%,520px)] bg-center bg-no-repeat opacity-[0.42] motion-safe:transition-opacity group-hover/card:opacity-[0.5]"
                                style={{ backgroundImage: `url(${zrsLogoUrl})` }}
                                role="img"
                                aria-label={t("servers.bannerAria")}
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-[#050505]/90 via-[#0c0c0f]/75 to-[#111214]" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#111214] via-transparent to-amber-400/10" />
                            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#111214] to-transparent" />
                        </div>

                        {/* Corpo: ícone “guild” sobreposto + nome */}
                        <div className="relative px-4 pb-5 pt-0 md:px-6 md:pb-6">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:gap-5 -mt-14 sm:-mt-16">
                                <div
                                    className="flex h-[5.25rem] w-[5.25rem] shrink-0 items-center justify-center rounded-[22px] border-4 border-[#111214] bg-gradient-to-br from-[#1a1a1f] to-[#0d0d10] shadow-[0_12px_40px_rgba(0,0,0,0.5)] ring-1 ring-white/[0.08]"
                                    aria-hidden
                                >
                                    <Server
                                        className="h-9 w-9 text-[#FFB800] drop-shadow-[0_0_12px_rgba(255,184,0,0.35)]"
                                        strokeWidth={1.75}
                                    />
                                </div>

                                <div className="min-w-0 flex-1 pb-0.5 sm:pb-1">
                                    <div className="flex flex-wrap items-center gap-2 gap-y-2">
                                        <h3 className="text-xl font-bold tracking-tight text-white md:text-2xl">
                                            {CARD_TITLE}
                                        </h3>
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]"
                                            style={{
                                                background: online
                                                    ? "rgba(68,210,100,0.12)"
                                                    : "rgba(239,68,68,0.14)",
                                                color: online ? "#7FD89B" : "#f87171",
                                                borderColor: online
                                                    ? "rgba(68,210,100,0.28)"
                                                    : "rgba(239,68,68,0.32)",
                                            }}
                                        >
                                            <span className="relative flex h-2 w-2">
                                                {online ? (
                                                    <>
                                                        <span className="absolute inset-0 rounded-full bg-[#44d264] status-pulse" />
                                                        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#44d264]" />
                                                    </>
                                                ) : (
                                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                                                )}
                                            </span>
                                            {online ? t("servers.online") : t("servers.offline")}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="my-5 h-px bg-white/[0.07]" />

                            {loading && !data ? (
                                <p className="text-[13px] text-zinc-500">{t("servers.loading")}</p>
                            ) : null}

                            {data ? (
                                <div className="space-y-0.5 rounded-xl border border-white/[0.05] bg-black/25 p-1">
                                    <StatRow icon={Users} label={t("servers.labelPlayers")} value={formatPlayers(data, t)} />
                                    <StatRow icon={Gauge} label={t("servers.labelRates")} value={data.ratesLabel} />
                                    {data.lootSpawnMultiplier != null ? (
                                        <StatRow
                                            icon={Package}
                                            label={t("servers.labelLootSpawn")}
                                            value={
                                                <span className="inline-flex items-center justify-end gap-1.5">
                                                    {data.lootSpawnMultiplier}x
                                                    {data.lootSpawnMultiplier > LOOT_BOOST_ABOVE ? (
                                                        <Flame
                                                            size={14}
                                                            className="text-[#FFB800]"
                                                            aria-label={t("servers.lootBoosted")}
                                                        />
                                                    ) : null}
                                                </span>
                                            }
                                        />
                                    ) : null}
                                    <StatRow icon={CalendarClock} label={t("servers.labelWipe")} value={formatWipe(data, t, lang)} />
                                    <StatRow icon={Activity} label={t("servers.labelUptime")} value={formatUptime(data, t)} />
                                    <StatRow
                                        icon={MapIcon}
                                        label={t("servers.labelMap")}
                                        value={
                                            data.mapLabel ? (
                                                <span className="flex flex-col items-end gap-0.5">
                                                    <span>{data.mapLabel}</span>
                                                    {data.mapUrl ? (
                                                        <a
                                                            href={data.mapUrl}
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            aria-label={t("servers.viewMapAria")}
                                                            data-testid="server-map-link"
                                                            className="inline-flex items-center gap-1 text-[12px] font-semibold text-gold hover:underline"
                                                        >
                                                            {t("servers.viewMap")}
                                                            <ExternalLink size={12} aria-hidden />
                                                        </a>
                                                    ) : null}
                                                </span>
                                            ) : (
                                                data.mapName
                                            )
                                        }
                                    />
                                </div>
                            ) : null}

                            {data?.connectCommand ? (
                                <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <button
                                        type="button"
                                        onClick={onConnect}
                                        aria-describedby="server-connect-command"
                                        data-testid="server-connect-btn"
                                        className="btn-gold btn-shimmer inline-flex shrink-0 items-center justify-center gap-2 rounded-xl px-5 py-3 text-[13px] font-bold uppercase tracking-[0.18em]"
                                    >
                                        <Terminal size={16} aria-hidden />
                                        {t("servers.connect")}
                                    </button>
                                    <code
                                        className="min-w-0 truncate rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2.5 font-mono text-[12px] text-zinc-400"
                                        id="server-connect-command"
                                        title={t("servers.connectHint")}
                                        data-testid="server-connect-command"
                                    >
                                        {data.connectCommand}
                                    </code>
                                </div>
                            ) : null}

                            {data?.stale ? (
                                <p className="mt-3 text-[11px] text-zinc-500">
                                    {t("servers.stale")}
                                </p>
                            ) : null}
                        </div>
                    </div>
                </ScrollReveal>
            </div>
        </section>
    );
}
